/**
 * 🏷️ 标签选择器（环艺学姐专属）
 *
 * 功能：
 * - 按标签组（颜色/材质/风格…）分区多选
 * - 支持直接输入新标签，回车即创建（需后端支持）
 * - 支持从标签字典中搜索
 * - 显示每个标签的使用次数
 */

import React, { useState, useMemo } from 'react';
import { X, Plus, Search } from 'lucide-react';

const T = {
  inputBg: 'var(--modal-input-bg)',
  border: 'var(--border-subtle)',
  hover: 'var(--bg-hover)',
  text: 'var(--text-primary)',
  text2: 'var(--text-secondary)',
  muted: 'var(--text-muted)'
};

const inputStyle = {
  width: '100%',
  padding: '8px',
  border: `1px solid ${T.border}`,
  borderRadius: '4px',
  fontSize: '13px',
  background: T.inputBg,
  color: T.text,
  boxSizing: 'border-box'
};

const TagPicker = ({
  groups = [],
  selected = [],            // 已选标签名数组
  onChange,                 // (newSelectedNames: string[]) => void
  onCreateTag,              // async (name, group) => 新标签对象；失败返回 null
  showCreate = true,
  maxHeight = 260
}) => {
  const [activeGroup, setActiveGroup] = useState(null);
  const [search, setSearch] = useState('');
  const [newTag, setNewTag] = useState('');
  const [creating, setCreating] = useState(false);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const visibleGroups = useMemo(() => {
    const q = search.trim();
    if (!q) return groups;
    return groups
      .map(g => ({
        ...g,
        tags: (g.tags || []).filter(t => t.name.toLowerCase().includes(q.toLowerCase()))
      }))
      .filter(g => g.tags.length > 0);
  }, [groups, search]);

  const filteredGroups = useMemo(() => {
    const source = activeGroup === null
      ? visibleGroups
      : visibleGroups.filter(g => g.name === activeGroup);
    return source;
  }, [activeGroup, visibleGroups]);

  const toggleTag = (name) => {
    const next = selectedSet.has(name)
      ? selected.filter(t => t !== name)
      : [...selected, name];
    onChange(next);
  };

  const handleCreate = async () => {
    const name = newTag.trim();
    if (!name || creating) return;
    if (selectedSet.has(name)) {
      setNewTag('');
      return;
    }
    setCreating(true);
    try {
      const created = await onCreateTag?.(name, activeGroup || '其他');
      if (created) {
        onChange([...selected, created.name || name]);
      } else {
        // 后端创建失败时，仍允许以纯文本形式加入（后端上传时会自动建条目）
        onChange([...selected, name]);
      }
    } finally {
      setCreating(false);
      setNewTag('');
    }
  };

  return (
    <div>
      {/* 已选标签 */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        marginBottom: '8px',
        minHeight: '26px'
      }}>
        {selected.length === 0 && (
          <span style={{ fontSize: '12px', color: T.muted }}>尚未选择标签</span>
        )}
        {selected.map(tag => (
          <span
            key={tag}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '3px 8px',
              fontSize: '12px',
              borderRadius: '12px',
              background: 'rgba(59,130,246,0.15)',
              border: '1px solid rgba(59,130,246,0.35)',
              color: '#60a5fa'
            }}
          >
            {tag}
            <button
              onClick={() => toggleTag(tag)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: 'inherit',
                display: 'flex'
              }}
            >
              <X size={11} />
            </button>
          </span>
        ))}
      </div>

      {/* 搜索 */}
      <div style={{ position: 'relative', marginBottom: '8px' }}>
        <Search
          size={13}
          style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: T.muted }}
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="搜索标签…"
          style={{ ...inputStyle, paddingLeft: '26px' }}
        />
      </div>

      {/* 标签组 Tab */}
      {groups.length > 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
          <button
            onClick={() => setActiveGroup(null)}
            style={{
              padding: '3px 10px',
              fontSize: '12px',
              borderRadius: '12px',
              cursor: 'pointer',
              border: `1px solid ${activeGroup === null ? '#3b82f6' : T.border}`,
              background: activeGroup === null ? 'rgba(59,130,246,0.15)' : 'transparent',
              color: activeGroup === null ? '#60a5fa' : T.text2
            }}
          >
            全部
          </button>
          {groups.map(g => (
            <button
              key={g.name}
              onClick={() => setActiveGroup(g.name)}
              style={{
                padding: '3px 10px',
                fontSize: '12px',
                borderRadius: '12px',
                cursor: 'pointer',
                border: `1px solid ${activeGroup === g.name ? '#3b82f6' : T.border}`,
                background: activeGroup === g.name ? 'rgba(59,130,246,0.15)' : 'transparent',
                color: activeGroup === g.name ? '#60a5fa' : T.text2
              }}
            >
              {g.name}
            </button>
          ))}
        </div>
      )}

      {/* 标签列表 */}
      <div style={{
        maxHeight,
        overflowY: 'auto',
        border: `1px solid ${T.border}`,
        borderRadius: '4px',
        padding: '8px'
      }}>
        {filteredGroups.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '16px', color: T.muted, fontSize: '13px' }}>
            {search ? '没有匹配的标签' : '暂无标签'}
          </div>
        ) : (
          filteredGroups.map(g => (
            <div key={g.name} style={{ marginBottom: '10px' }}>
              <div style={{
                fontSize: '11px',
                fontWeight: 600,
                color: T.muted,
                marginBottom: '6px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {g.name}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {(g.tags || []).map(t => {
                  const isOn = selectedSet.has(t.name);
                  return (
                    <button
                      key={t.id}
                      onClick={() => toggleTag(t.name)}
                      style={{
                        padding: '3px 9px',
                        fontSize: '12px',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        border: `1px solid ${isOn ? '#3b82f6' : T.border}`,
                        background: isOn ? 'rgba(59,130,246,0.15)' : T.hover,
                        color: isOn ? '#60a5fa' : T.text2,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      {t.name}
                      {t.usage_count > 0 && (
                        <span style={{ fontSize: '10px', opacity: 0.7 }}>{t.usage_count}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {/* 新建标签 */}
      {showCreate && (
        <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
          <input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreate(); } }}
            placeholder="输入新标签后回车创建"
            style={inputStyle}
          />
          <button
            onClick={handleCreate}
            disabled={!newTag.trim() || creating}
            style={{
              padding: '8px 12px',
              background: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: (!newTag.trim() || creating) ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              whiteSpace: 'nowrap'
            }}
          >
            <Plus size={13} />
            新建
          </button>
        </div>
      )}
    </div>
  );
};

export default TagPicker;
